<template>
  <d-container
    fluid
    class="main-content-container px-4">
    <!-- Page Header -->
    <d-row
      no-gutters
      class="page-header py-4">
      <d-col
        col
        sm="6"
        class="text-center text-sm-left mb-4 mb-sm-0">
        <span class="text-uppercase page-subtitle">
          {{ $t('product.products') }}
        </span>
        <h3 class="page-title">
          <template v-if="isIndentList">
            {{ $t('product.productsIndentList') }}
          </template>
          <template v-else-if="isApprovalList">
            {{ $t('product.productsApprovalList') }}
          </template>
          <template v-else>
            {{ $t('product.productsList') }}
          </template>
        </h3>
      </d-col>
      <d-col
        col
        sm="6"
        class="text-right text-sm-right d-flex justify-content-end align-items-end">
        <d-button
          v-if="!isApprovalList"
          theme="success"
          @click.prevent="$router.push(addNewUrl)">
          <i class="material-icons">add</i>
          <template v-if="isIndentList">
            {{ $t('product.addNewIndentProduct') }}
          </template>
          <template v-else>
            {{ $t('product.addNewProduct') }}
          </template>
        </d-button>
      </d-col>
    </d-row>

    <d-row
      style="width: 100%;"
      class="d-flex ml-0 mb-3 page-filters">
      <validation-observer
        ref="products-filters-form"
        tag="form"
        style="width: 100%;"
        class="d-flex">
        <d-input
          :placeholder="$t('product.enterProductNameOrCode')"
          v-model="filters.search" />

        <v-select
          v-if="!isApprovalList"
          style="width: 400px;"
          class="ml-2"
          :options="availabilityOptions"
          v-model="filters.availability"
          :placeholder="$t('product.productAvailability')"
          :searchable="false"
          label="text">
        </v-select>

        <v-select
          v-if="!isApprovalList"
          style="width: 400px;"
          class="ml-2"
          :options="categoriesStructure"
          v-model="filters.category_id"
          :reduce="category => category.id"
          :placeholder="$t('productCategories.selectMainCategory')"
          label="name"
          :clearable="true">
          <template slot="no-options">
            {{ $t('productCategories.noCategoriesToDisplay') }}&hellip;
          </template>
          <template slot="option" slot-scope="option">
            <div class="d-center" v-pure-html="option.name"></div>
          </template>
          <template slot="selected-option" slot-scope="option">
            <div class="selected d-center" v-pure-html="option.name"></div>
          </template>
        </v-select>

        <d-button
          theme="info"
          outline
          class="text-nowrap ml-2"
          @click.prevent="validateFiltersForm">
          {{ $t('ui.search') }}
        </d-button>

        <d-button
          theme="danger"
          outline
          class="text-nowrap ml-2"
          @click.prevent="clearFilters">
          {{ $t('ui.resetFilters') }}
        </d-button>
      </validation-observer>
    </d-row>

    <d-card style="width: 100%;">
      <table class="table mt-0 mb-0 full-width">
        <thead class="bg-light">
          <tr>
            <th scope="col" class="border-0" style="width: 80px;">{{ $t('image.photo') }}</th>
            <th scope="col" class="border-0">{{ $t('product.product') }}</th>
            <th scope="col" class="border-0" style="min-width: 200px;">{{ $t('product.productCode') }}</th>
            <template v-if="!isIndentList && !isApprovalList">
              <th scope="col" class="border-0 text-center">{{ $t('product.variantsCount') }}</th>
            </template>
            <th v-if="!isApprovalList" scope="col" class="border-0" style="min-width: 120px;">{{ $t('product.piecesInBox') }}</th>
            <template v-if="isIndentList">
              <th scope="col" class="border-0" style="min-width: 240px;">{{ $t('product.indentProduct.properties') }}</th>
            </template>
            <th v-if="isApprovalList" style="min-width: 240px;"></th>
            <th v-if="!isApprovalList" scope="col" class="border-0" style="min-width: 120px;">{{ $t('price.prices') }}</th>
            <th v-if="!isApprovalList" scope="col" class="border-0">{{ $t('product.availableQuantity') }}</th>
            <th scope="col" class="border-0">{{ $t('ui.actions') }}</th>
          </tr>
        </thead>
        <tbody v-if="!loadError">
          <tr
            v-for="(product, index) of products"
            :key="'product-' + index">
            <td class="align-middle">
              <img
                v-if="product.image_url"
                :src="product.image_url"
                alt=""
                class="product-image" />
              <span
                v-else
                class="product-empty-image">
                <i class="material-icons">camera_alt</i>
              </span>
            </td>
            <td class="align-middle">
              {{ product.name }}
              <br v-if="product.available_in_purchase">
              <d-badge
                v-if="product.available_in_purchase"
                theme="success">
                {{ $t('product.availableInPurchases') }}
              </d-badge>
              <d-badge
                v-if="product.is_sale"
                theme="warning"
                class="ml-1">
                {{ $t('product.isSale') }}
              </d-badge>
              <br>
              <small>{{ $t('ui.created') }}: {{ product.created_at }}</small>
              <br v-if="product.default_services && product.default_services.length">
              <small
                v-if="product.default_services && product.default_services.length"
                class="text-primary">
                {{ $t('product.productIncludesDefaultServices') }}: {{ product.default_services.length }}
              </small>
              <br v-if="product.category">
              <small v-if="product.category">
                {{ $t('productCategories.mainCategory') }}:
                <span class="text-primary">
                  {{ product.category.name }}
                </span>
              </small>
              <br>
              <small>
                {{ $t('product.minimalQty') }}:
                <span class="text-primary">
                  {{ product.minimum_quantity }} {{ $t('ui.piecesAbbr') }}
                </span>
              </small>
              <template v-if="!isIndentList && product.cn_code">
                <br>
                <small>
                  {{ $t('product.indentProduct.cnCode') }}:
                </small>
                <small class="text-primary">
                  {{ product.cn_code }}
                </small>
              </template>
            </td>
            <td class="align-middle">
              {{ product.code }}
            </td>
            <td
              v-if="!isIndentList && !isApprovalList"
              class="align-middle text-center">
              {{ product.children_count }}
            </td>
            <td
              v-if="!isApprovalList"
              class="align-middle">
              {{ product.pieces_in_carton }} {{ $t('ui.piecesAbbr') }}<br>
              <small>({{ $t('product.actually') }}: {{ product.real_pieces_in_carton }} {{ $t('ui.piecesAbbr') }})</small>
            </td>
            <template v-if="isIndentList">
              <td class="align-middle">
                {{ $t('product.productIndentBaseProduct') }}: <strong>{{ product.base_product_name }}</strong><br>
                {{ $t('product.indentProduct.weight') }}: <strong>{{ product.weight }} kg</strong><br>
                {{ $t('product.indentProduct.cnCode') }}: <strong>{{ product.cn_code }}</strong><br>
                {{ $t('product.indentProduct.originCountry') }}: <strong>{{ product.origin_country }}</strong>
              </td>
            </template>
            <td
              v-if="!isApprovalList"
              class="align-middle">
              <template v-for="(price, index) of product.prices">
                <span :key="'product-price-' + index + '-' + product.id" class="product-price">
                  {{ Number(price.price).toFixed(2) }} {{ getCurrency(price.currency_id) }}
                </span>
              </template>
            </td>
            <td
              v-if="!isApprovalList"
              class="align-middle">
              {{ product.availability }}
            </td>

            <td
              v-if="isApprovalList"
              class="align-middle">
              <div>
                <strong>
                  {{ $t('product.properties.material') }}:
                </strong>
                <span
                  v-if="product.color_id"
                  class="text-primary">
                  {{ getMaterialNameByColor(product.color_id) }}
                </span>
                <span
                  v-else
                  class="text-primary">
                  {{ $t('ui.lack') }}
                </span>
              </div>

              <div>
                <strong>
                  {{ $t('product.properties.color') }}:
                </strong>
                <span class="text-primary">
                  <template v-if="colorsList[product.color_id]">
                    {{ colorsList[product.color_id] }}
                  </template>
                  <template v-else-if="!product.color_id">
                    {{ $t('ui.lack') }}
                  </template>
                  <template v-else>
                    --
                  </template>
                </span>
              </div>

              <div>
                <strong>
                  {{ $t('product.properties.strand') }}:
                </strong>
                <span class="text-primary">
                  <template v-if="strandsList[product.strand_id]">
                    {{ strandsList[product.strand_id] }}
                  </template>
                  <template v-else-if="!product.strand_id">
                    {{ $t('ui.lack') }}
                  </template>
                  <template v-else>
                    --
                  </template>
                </span>
              </div>

              <div>
                <strong>
                  {{ $t('product.properties.sizes') }}:
                </strong>
                <span
                  v-for="(size, index) of product.sizes"
                  :key="'product-size-' + index + '-' + product.id"
                  class="text-primary">
                  <template v-if="sizesList[size]">
                    {{ sizesList[size] }}<template v-if="index < product.sizes.length - 1">,</template>
                  </template>
                  <template v-else>
                    --
                  </template>
                </span>

                <span
                  v-if="!product.sizes.length"
                  class="text-primary">
                  {{ $t('ui.lack') }}
                </span>
              </div>

              <div>
                <strong>
                  {{ $t('product.properties.procedures') }}:
                </strong>
                <span
                  v-for="(procedure, index) of product.procedures"
                  :key="'product-procedure-' + index + '-' + product.id"
                  class="text-primary">
                  <template v-if="proceduresList[procedure]">
                    {{ proceduresList[procedure] }}<template v-if="index < product.procedures.length - 1">,</template>
                  </template>
                  <template v-else>
                    --
                  </template>
                </span>

                <span
                  v-if="!product.procedures.length"
                  class="text-primary">
                  {{ $t('ui.lack') }}
                </span>
              </div>
            </td>

            <td class="align-middle" style="width: 190px;">
              <d-button
                v-if="isApprovalList"
                theme="success"
                @click.native="approveProduct(product.id)"
                class="mr-2"
                :disabled="approvalInProgress"
                :title="$t('product.approve')">
                <div
                  v-if="approvalInProgress === product.id"
                  class="spinner-border spinner-border-sm"
                  role="status"></div>
                <template v-else>
                  <i class="material-icons">
                    check
                  </i>
                  {{ $t('product.approve') }}
                </template>
              </d-button>
              <d-button
                v-if="!isApprovalList"
                theme="primary"
                @click.native="$router.push(editUrl + product.id)"
                class="mr-2"
                :title="$t('ui.edit')">
                <i class="material-icons">edit</i>
              </d-button>
              <d-button
                v-if="!isApprovalList"
                theme="secondary"
                @click.native="duplicateProduct(product.id)"
                class="mr-2"
                :disabled="!!duplicateInProgress"
                :title="$t('ui.duplicate')">
                <div
                  v-if="duplicateInProgress === product.id"
                  class="spinner-border spinner-border-sm"
                  role="status"></div>
                <i
                  v-else
                  class="material-icons">
                  file_copy
                </i>
              </d-button>
              <d-button
                v-if="!isApprovalList"
                theme="danger"
                :disabled="removeInProgress"
                @click.native="removeProduct(product.id)"
                :title="$t('ui.delete')">
                <i class="material-icons">delete</i>
              </d-button>
            </td>
          </tr>
          <tr v-if="products.length === 0">
            <td
              :colspan="isApprovalList ? 5 : 8"
              class="text-center">
              {{ $t('product.noProductsToDisplay') }}&hellip;
            </td>
          </tr>
        </tbody>
      </table>

      <div
        v-if="!allDataLoaded"
        class="pb-3 pt-3 text-center overlay-loading">
        {{ $t('ui.dataLoading') }}&hellip;
      </div>

      <d-alert
        v-if="loadError"
        show
        theme="warning">
        {{ $t('ui.dataLoadingFailed') }} <a href="javascript:window.location.reload();" class="alert-link">{{ $t('ui.refreshPage') }}</a> {{ $t('ui.toTryAgain') }}
      </d-alert>
    </d-card>

    <pagination
      :meta-data="paginationMetaData"
      :initial-page="currentPage" />
  </d-container>
</template>

<script>
import debounce from 'lodash.debounce';
import CommonGetters from './../utils/CommonGetters.js';
import HasFilters from './../mixins/HasFilters.vue';
import HasPagination from './../mixins/HasPagination.vue';
import ListUtils from './../utils/ListUtils.js';
import FormUtils from './../utils/FormUtils.js';
import Pagination from './../components/common/Pagination.vue';

export default {
  name: 'products',
  mixins: [
    HasFilters,
    HasPagination
  ],
  components: {
    'pagination': Pagination
  },
  computed: {
    allDataLoaded () {
      if (this.isApprovalList) {
        return this.loaded && this.loadedCurrencies && this.loadedCategories && this.loadedSewingRoomItems;
      }

      return this.loaded && this.loadedCurrencies && this.loadedCategories;
    },
    availabilityOptions () {
      return [
        {
          text: this.$t('product.allProducts'),
          value: 0
        },
        {
          text: this.$t('product.lowAvailability'),
          value: 1
        }
      ];
    },
    isIndentList () {
      return this.$route.path.indexOf('/produkty-indent') > -1;
    },
    isApprovalList () {
      return this.$route.path.indexOf('/produkty-do-zatwierdzenia-ze-szwalni') > -1;
    },
    editUrl () {
      if (this.isIndentList) {
        return '/produkty-indent/edycja/';
      }

      return '/produkty/edycja/';
    },
    addNewUrl () {
      if (this.isIndentList) {
        return '/produkty-indent/nowy';
      }

      return '/produkty/nowy';
    },
    categoriesStructure () {
      let result = [];
      let stack = this.categoriesList.map(category => ({ ...category, prefix: '' }));

      while (stack.length > 0) {
        let current = stack.shift();

        result.push({
          id: current.id,
          name: current.prefix + current.name
        });

        if (current.children && current.children.length > 0) {
          stack = [
            ...current.children.map(child => ({ ...child, prefix: current.prefix + '<span style="color: #aaa;">&ndash;</span> ' })),
            ...stack
          ];
        }
      }

      return result;
    },
    strandsList () {
      let output = {};

      if (this.productProperties && this.productProperties.strands) {
        for (let i = 0; i < this.productProperties.strands.length; i++) {
          let strand = this.productProperties.strands[i];
          output[strand.id] = strand.name;
        }
      }

      return output;
    },
    sizesList () {
      let output = {};

      if (this.productProperties && this.productProperties.sizes) {
        for (let i = 0; i < this.productProperties.sizes.length; i++) {
          let size = this.productProperties.sizes[i];
          output[size.id] = size.name;
        }
      }

      return output;
    },
    proceduresList () {
      let output = {};

      if (this.productProperties && this.productProperties.procedures) {
        for (let i = 0; i < this.productProperties.procedures.length; i++) {
          let procedure = this.productProperties.procedures[i];
          output[procedure.id] = procedure.name;
        }
      }

      return output;
    },
    colorsList () {
      if (this.productProperties && this.productProperties.materials) {
        let allColors = {};
        let addedColors = [];
        let colors = this.productProperties.materials.map(material => material.colors);

        for (let i = 0; i < colors.length; i++) {
          for (let j = 0; j < colors[i].length; j++) {
            if (addedColors.indexOf(colors[i][j].id) === -1) {
              allColors[colors[i][j].id] = colors[i][j].name;
              addedColors.push(colors[i][j].id);
            }
          }
        }

        return allColors;
      }

      return [];
    }
  },
  data() {
    return {
      products: [],
      currencies: [],
      loaded: false,
      loadError: false,
      loadedCurrencies: false,
      removeInProgress: false,
      approvalInProgress: false,
      duplicateInProgress: false,
      categoriesList: [],
      loadedCategories: false,
      loadedSewingRoomItems: false,
      productProperties: null,
      filters: {
        search: '',
        availability: '',
        category_id: null
      }
    };
  },
  mounted () {
    this.loadFilteredData();
    this.loadAdditionalData();
  },
  methods: {
    debouncedLoadFilteredData: debounce(function () {
      this.$bus.$emit('view-filters-save');
      this.loadFilteredData();
    }, 250),
    loadFilteredData () {
      let where = {
        search: this.filters.search,
        is_indent: this.isIndentList ? 1 : 0
      };

      if (!this.isApprovalList && this.filters.availability !== null && this.filters.availability && typeof this.filters.availability.value === 'number') {
        where.low_availability = this.filters.availability.value;
      }

      if (!this.isApprovalList && !this.isIndentList) {
        where.parent_id = 0;
      }

      if (!this.isApprovalList && this.filters.category_id !== null) {
        where.category_id = this.filters.category_id;
      }

      let endpointBase = '/products/items';

      if (this.isApprovalList) {
        endpointBase = '/products/items-waiting-sewingroom';
      }

      ListUtils.loadPaginatedItemsData(this, {
        method: 'get',
        endpoint: endpointBase + '?page=' + this.currentPage + '&lang=' + this.$store.getters.getUserLang,
        listField: 'products',
        params: {
          with: ['productPrices', 'defaultServices', 'category'],
          where
        }
      });
    },
    clearFilters () {
      this.loaded = false;
      this.loadError = false;
      this.filters = {
        search: '',
        availability: '',
        category_id: null
      };

      this.$bus.$emit('view-filters-reset');
      this.$bus.$emit('pagination-reset', false);
    },
    validateFiltersForm () {
      this.$bus.$emit('pagination-reset');
      FormUtils.validate(this.$refs['products-filters-form'], this.debouncedLoadFilteredData);
    },
    loadAdditionalData () {
      FormUtils.loadAdditionalData(this, {
        method: 'get',
        endpoint: '/currencies/items',
        loadedKey: 'loadedCurrencies',
        outputKey: 'currencies',
        errorKey: 'loadError'
      });

      FormUtils.loadAdditionalData(this, {
        method: 'get',
        endpoint: '/categories/items',
        loadedKey: 'loadedCategories',
        outputKey: 'categoriesList',
        errorKey: 'loadError'
      });

      if (this.isApprovalList) {
        FormUtils.loadAdditionalData(this, {
          endpoint: '/sewing-room/items',
          loadedKey: 'loadedSewingRoomItems',
          outputKey: 'productProperties',
          errorKey: 'loadError'
        });
      }
    },
    approveProduct (productID) {
      this.approvalInProgress = productID;

      FormUtils.submitInBackground({
        $http: this.$http,
        endpoint: '/products/accept-sewingroom/' + parseInt(productID, 10),
        formData: null,
        errorTitle: this.$t('ui.errorOccured'),
        errorText: this.$t('ui.tryAgain'),
        successAction: () => {
          this.approvalInProgress = false;
          this.loadFilteredData();
        },
        failAction: () => {
          this.approvalInProgress = false;
        }
      });
    },
    removeProduct (productID) {
      this.removeInProgress = true;

      ListUtils.removeItem({
        $http: this.$http,
        endpointBase: '/products/delete/',
        id: productID,
        successTitle: this.$t('product.removeProductSuccessTitle'),
        successText: this.$t('product.removeProductSuccessText'),
        confirmTitle: this.$t('product.removeProductConfirmTitle'),
        confirmText: this.$t('ui.deleteConfirmText'),
        errorTitle: this.$t('ui.errorOccured'),
        errorText: this.$t('product.removeProductErrorText'),
        successAction: () => {
          this.removeInProgress = false;
          this.loadFilteredData();
        },
        cancelAction: () => {
          this.removeInProgress = false;
        }
      });
    },
    duplicateProduct (productID) {
      this.duplicateInProgress = productID;

      FormUtils.submitInBackground({
        $http: this.$http,
        endpoint: '/products/duplicate/' + parseInt(productID, 10),
        formData: null,
        errorTitle: this.$t('ui.errorOccured'),
        errorText: this.$t('product.productDuplicateErrorText'),
        successAction: (response) => {
          let redirectUrl = '/produkty/edycja/' + parseInt(response.id, 10);

          if (this.isIndentList) {
            redirectUrl = '/produkty-indent/edycja/' + parseInt(response.id, 10);
          }

          this.duplicateInProgress = false;
          this.$router.push(redirectUrl);
        },
        failAction: () => {
          this.duplicateInProgress = false;
        }
      });
    },
    getCurrency (currencyID) {
      return CommonGetters.getCurrencyById(currencyID, this.currencies);
    },
    getMaterialNameByColor (colorID) {
      if (this.productProperties && this.productProperties.materials) {
        for (let i = 0; i < this.productProperties.materials.length; i++) {
          let material = this.productProperties.materials[i];
          let colors = material.colors;

          for (let j = 0; j < colors.length; j++) {
            if (colors[j].id === colorID) {
              return material.name;
            }
          }
        }
      }

      return '--';
    }
  }
};
</script>

<style lang="scss" scoped>
.product {
  &-image {
    height: auto;
    max-height: 80px;
    max-width: 80px;
    width: auto;
  }

  &-empty-image {
    align-items: center;
    background: #e5e5e5;
    color: #fff;
    display: flex;
    font-size: 48px;
    height: 80px;
    justify-content: center;
    width: 80px;
  }

  &-price {
    display: block;
    font-size: 12px;
  }
}
</style>

